/**
 * Quality Command Center — dedicated run-detail route (/admin/quality/runs/:runId).
 * Admin-only (route-guarded + in-page re-check). Read-only: shows identity, scope,
 * scenarios (filter + search), findings, cleanup evidence, replay metadata (copy only —
 * never executes), and a safe sanitized artifact viewer. No absolute filesystem paths.
 */
import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';
import { useQaRun, useQaScenariosForRun, useQaFindingsForRun } from '@/hooks/useQualityCommandCenter';
import { normResult, artifactHealthClass, profileLabel } from '@/lib/qa/commandCenterLogic';
import { ResultBadge, Loading, ErrorState, Empty, fmtDuration, ageLabel } from './shared';

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); }); }} aria-label="Copy replay command">
      {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {done ? 'Copied' : 'Copy'}
    </Button>
  );
}

/** Strip anything path-like before showing artifact JSON (defense-in-depth; already sanitized at ingest). */
function safeJson(obj: unknown): string {
  const json = JSON.stringify(obj, null, 2);
  return json.replace(/("(?:[^"]*\/)+[^"]*")/g, '"[path-redacted]"');
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="text-sm"><span className="text-muted-foreground">{label}: </span><span className="font-medium">{children}</span></div>;
}

export default function RunDetail() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { data: isAdmin, isLoading: adminLoading } = useIsInternalAdmin();
  const run = useQaRun(runId);
  const scenarios = useQaScenariosForRun(runId ?? null);
  const findings = useQaFindingsForRun(runId);
  const [scenFilter, setScenFilter] = useState('');
  const [scenResult, setScenResult] = useState('ALL');
  const [showArtifacts, setShowArtifacts] = useState(false);

  const filteredScenarios = useMemo(() => {
    const list = (scenarios.data ?? []) as any[];
    return list.filter((s) => (scenResult === 'ALL' || normResult(s.status) === scenResult) && (!scenFilter || s.name.toLowerCase().includes(scenFilter.toLowerCase()) || (s.detail ?? '').toLowerCase().includes(scenFilter.toLowerCase())));
  }, [scenarios.data, scenFilter, scenResult]);

  if (adminLoading) return <Loading />;
  if (!isAdmin) return <div className="mx-auto max-w-lg py-24 text-center"><h1 className="text-lg font-semibold">403 — Access denied</h1><p className="text-sm text-muted-foreground">Platform administrators only.</p></div>;
  if (run.isLoading) return <Loading />;
  if (run.error) return <ErrorState e={run.error} />;
  if (!run.data) return <div className="mx-auto max-w-3xl py-12"><Button variant="ghost" size="sm" onClick={() => navigate('/admin/quality')}><ArrowLeft className="h-4 w-4" /> Back</Button><Empty what={`run "${runId}"`} /></div>;

  const r = run.data;
  const prof = profileLabel(r.engine, r.scenario);
  const health = r.artifact_health;
  const age = ageLabel(r.finished_at);
  const RESULT_STATES = ['ALL', 'PASS', 'FAIL_PRODUCT', 'FAIL_HARNESS', 'BLOCKED_ENV', 'BLOCKED_PRODUCT', 'NOT_EXECUTED', 'UNKNOWN', 'ARTIFACT_ERROR'];

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/quality')}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="min-w-0">
          <h1 className="text-lg font-bold flex items-center gap-2 truncate"><span className="font-mono">{r.run_id}</span> <ResultBadge result={normResult(r.result)} /></h1>
          <p className="text-sm text-muted-foreground capitalize">{r.engine} · {prof.label}{!prof.full && <Badge variant="outline" className="ml-2 border-amber-400 text-amber-700 text-[10px]">reduced-depth profile</Badge>}</p>
        </div>
      </div>

      {/* Identity */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Identity</CardTitle></CardHeader>
        <CardContent className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Engine">{r.engine}</Field>
          <Field label="Result">{normResult(r.result)}</Field>
          <Field label="Trust">{prof.full ? 'full-depth profile' : 'smoke/small profile — not full-depth'}</Field>
          <Field label="Started">{r.started_at ? new Date(r.started_at).toLocaleString() : 'n/a'}</Field>
          <Field label="Finished">{r.finished_at ? new Date(r.finished_at).toLocaleString() : 'n/a'} ({age.text}){age.stale && <Badge variant="outline" className="ml-1 border-amber-400 text-amber-700 text-[10px]">stale</Badge>}</Field>
          <Field label="Duration">{fmtDuration(r.duration_ms)}</Field>
          <Field label="Environment">{r.environment ?? 'n/a'}</Field>
          <Field label="Seed">{r.seed || 'n/a'}</Field>
          <Field label="Schema version">{r.schema_version ?? 'n/a'}</Field>
          <Field label="Source fingerprint"><code className="text-xs">{r.fingerprint ?? 'unknown'}</code></Field>
          <Field label="Working tree">{r.fingerprint?.includes('dirty') ? 'dirty (uncommitted changes)' : r.fingerprint ? 'clean' : 'unknown'}</Field>
          <Field label="Executed profile">{prof.label}</Field>
          {(r as { logical_run_id?: string }).logical_run_id && (
            <Field label="Logical run"><code className="text-xs">{(r as { logical_run_id?: string }).logical_run_id}</code></Field>
          )}
          {(r as { attempt_number?: number }).attempt_number != null && (
            <Field label="Attempt">
              #{(r as { attempt_number?: number }).attempt_number}
              {(r as { is_authoritative?: boolean }).is_authoritative
                ? <Badge variant="outline" className="ml-2 border-emerald-400 text-emerald-700 text-[10px]">authoritative</Badge>
                : <Badge variant="outline" className="ml-2 border-muted-foreground/40 text-muted-foreground text-[10px]">superseded</Badge>}
            </Field>
          )}
        </CardContent>
      </Card>

      {/* Autonomous-loop terminal outcome (meta/loop runs only) */}
      {(r.metrics as { loop?: Record<string, unknown> } | null)?.loop && (() => {
        const lp = (r.metrics as { loop: Record<string, string | number | null> }).loop;
        return (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Autonomous loop outcome</CardTitle><CardDescription>Seven orthogonal axes reported by the controller — the Command Center only displays them. QA-system health is tracked separately from product truth; the blind verifier is authoritative for the product axis.</CardDescription></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-1.5 sm:grid-cols-3">
                <Field label="Explorer">{lp.explorerOutcome ?? 'n/a'}</Field>
                <Field label="Repair">{lp.repairOutcome ?? 'n/a'}</Field>
                <Field label="Verifier"><span className={String(lp.verifierOutcome) === 'REJECT' ? 'text-red-700 font-semibold' : ''}>{lp.verifierOutcome ?? lp.verification ?? 'n/a'}</span></Field>
              </div>
              <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-4 border-t pt-3">
                <Field label="Product outcome"><Badge variant="outline" className={/VERIFIED|CHARTER_COMPLETE|NOT_REPRODUCED/.test(String(lp.productOutcome)) ? 'border-emerald-400 text-emerald-700' : 'border-amber-400 text-amber-700'}>{lp.productOutcome ?? 'n/a'}</Badge></Field>
                {lp.qaSystemOutcome != null && (
                  <Field label="QA system"><Badge variant="outline" className={String(lp.qaSystemOutcome) === 'OPERATIONAL' ? 'border-emerald-400 text-emerald-700' : 'border-red-400 text-red-700'}>{lp.qaSystemOutcome}</Badge></Field>
                )}
                <Field label="Execution">{lp.executionOutcome ?? 'n/a'}</Field>
                <Field label="Reporting">{lp.reportingOutcome ?? 'n/a'}</Field>
              </div>
              {String(lp.repairCharterEligible) === 'true' && (
                <div className="text-xs text-amber-700">⚑ Confirmed open defect — eligible to seed a later repair charter.</div>
              )}
              <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-4 border-t pt-3 text-sm">
                <Field label="Event count">{lp.eventCount ?? 'n/a'}</Field>
                <Field label="Patch"><code className="text-xs">{lp.patchCommit ?? 'none'}</code></Field>
                <Field label="Cleanup residue">{lp.cleanupResidue ?? 'n/a'}</Field>
                <Field label="Terminal state">{lp.terminal ?? 'n/a'}</Field>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Scope */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Scope</CardTitle><CardDescription>Executed vs blocked vs not-executed — from the run buckets.</CardDescription></CardHeader>
        <CardContent className="grid gap-1.5 sm:grid-cols-2 text-xs">
          {Object.entries(r.buckets ?? {}).map(([k, v]) => <div key={k}><span className="text-muted-foreground">{k}: </span><span className="font-medium">{(v as string[]).length}</span>{(v as string[]).length > 0 && k !== 'pass' && <span className="text-muted-foreground"> — {(v as string[]).slice(0, 3).join('; ')}{(v as string[]).length > 3 ? '…' : ''}</span>}</div>)}
          <div className="sm:col-span-2 text-muted-foreground">Cross-referenced / inherited evidence and explicit limitations for this engine are listed under the not-executed bucket above and in the engine's readiness artifact.</div>
        </CardContent>
      </Card>

      {/* Scenarios with filter + search */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Scenarios ({filteredScenarios.length}/{scenarios.data?.length ?? 0})</CardTitle>
          <div className="flex flex-wrap gap-2 pt-2">
            <Input placeholder="Search scenarios…" value={scenFilter} onChange={(e) => setScenFilter(e.target.value)} className="h-8 max-w-xs" aria-label="Search scenarios" />
            <select value={scenResult} onChange={(e) => setScenResult(e.target.value)} className="h-8 rounded-md border bg-background px-2 text-sm" aria-label="Filter by result">
              {RESULT_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {scenarios.isLoading ? <Loading /> : !filteredScenarios.length ? <Empty what="scenario" /> : (
            <Table>
              <TableHeader><TableRow><TableHead>Scenario</TableHead><TableHead>Result</TableHead><TableHead>Detail (assertions/invariants where present)</TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredScenarios.map((s: any) => (
                  <TableRow key={s.id}>
                    <TableCell className="text-sm max-w-md">{s.name}</TableCell>
                    <TableCell><ResultBadge result={normResult(s.status)} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.detail || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <p className="text-xs text-muted-foreground mt-2">Per-scenario duration, actor/role, and dedicated seed are not captured by the source harness; assertion/invariant info appears inline in Detail where the engine recorded it.</p>
        </CardContent>
      </Card>

      {/* Findings */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Findings linked to this run ({findings.data?.length ?? 0})</CardTitle></CardHeader>
        <CardContent>
          {findings.isLoading ? <Loading /> : !findings.data?.length ? <div className="text-sm text-muted-foreground">No findings recorded against this run.</div> : (
            <div className="space-y-2">{findings.data.map((f) => <div key={f.finding_id} className="text-sm"><Badge variant="outline" className="mr-2">{f.classification ?? f.severity ?? 'finding'}</Badge>{f.title ?? f.finding_id}{f.root_cause && <span className="text-muted-foreground"> — {f.root_cause}</span>}</div>)}</div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Cleanup */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Cleanup evidence</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Field label="Attempted">{r.cleanup_detail?.status ? 'yes' : 'unknown'}</Field>
            <Field label="Completed">{(r.cleanup_status ?? '').toUpperCase() === 'CLEAN' ? 'yes' : (r.cleanup_status ?? 'unknown')}</Field>
            <Field label="Independent residue check">{r.residue != null ? 'performed' : 'not recorded'}</Field>
            <Field label="Residue count">{r.residue ?? 'n/a'}</Field>
            <Field label="Retained fixtures">{r.cleanup_detail?.retained ?? 'none recorded'}</Field>
            <Field label="Cleanup errors">{r.cleanup_detail?.errors ?? 'none recorded'}</Field>
            <Field label="Final classification"><ResultBadge result={(r.cleanup_status ?? '').toUpperCase() === 'CLEAN' && (r.residue ?? 0) === 0 ? 'PASS' : 'UNKNOWN'} /></Field>
          </CardContent>
        </Card>

        {/* Replay */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Replay metadata</CardTitle><CardDescription>Copy only — the browser never executes commands.</CardDescription></CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            <Field label="Replay supported">{r.replay?.command ? 'yes (deterministic seed)' : 'unknown'}</Field>
            <Field label="Seed">{r.replay?.seed ?? r.seed ?? 'n/a'}</Field>
            <Field label="Source requirements">local repo @ fingerprint {r.fingerprint ?? 'unknown'}</Field>
            <Field label="Environment requirements">{r.environment ?? 'local'} Supabase</Field>
            {r.replay?.command && <div className="flex items-center gap-2 pt-1"><code className="text-xs bg-muted rounded px-2 py-1 flex-1 overflow-x-auto">{r.replay.command}</code><CopyButton text={r.replay.command} /></div>}
            <div className="text-xs text-muted-foreground">Limitation: replay reproduces the DB-layer run only; browser/long-duration paths are not replayed.</div>
          </CardContent>
        </Card>
      </div>

      {/* Artifacts */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center justify-between">Artifacts
          <Button variant="ghost" size="sm" onClick={() => setShowArtifacts((v) => !v)}>{showArtifacts ? 'Hide' : 'View sanitized evidence'}</Button></CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm mb-2"><span className="text-muted-foreground">Bundle health:</span> <ResultBadge result={artifactHealthClass(health)} />
            <span className="text-xs text-muted-foreground">{health?.present?.length ?? 0}/{health?.expected ?? '?'} present{health?.missing?.length ? ` · missing: ${health.missing.join(', ')}` : ''}{health?.malformed?.length ? ` · malformed: ${health.malformed.join(', ')}` : ''}</span></div>
          {showArtifacts && (
            <div className="space-y-2">
              {(['replay', 'cleanup_detail', 'metrics', 'artifact_health'] as const).map((key) => (
                <details key={key} className="text-xs border rounded-md">
                  <summary className="cursor-pointer px-2 py-1 font-medium">{key}</summary>
                  <pre className="overflow-x-auto p-2 bg-muted/40 text-[11px]">{safeJson((r as any)[key])}</pre>
                </details>
              ))}
              <p className="text-xs text-muted-foreground">Values under secret-like keys are redacted at ingest; absolute filesystem paths are additionally masked before display.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
